// src/services/client-ai-api.ts

// !!! WARNING !!!
// This file makes direct calls to the Google Generative Language API from the client-side.
// This approach uses Vite's environment variable handling (import.meta.env) which is safer 
// than hardcoding but still exposes the key in the bundled client code.
// DO NOT use this approach in production if the key needs to be kept absolutely secret.
// The backend proxy approach (e.g., /api/gemini) is the recommended secure method for production.

import { MODELS } from '@/config/ai-models'; // <-- Import MODELS

// Access the API key from environment variables (requires VITE_ prefix)
const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY;

interface GeminiResponse {
    candidates: {
        content: {
            parts: {
                text: string;
            }[];
        };
        // Other candidate properties might exist
    }[];
    // Other response properties might exist
}

// Get the default model ID from the central config
const DEFAULT_CLIENT_GEMINI_MODEL_ID = MODELS.gemini.find(m => m.id === 'gemini-2.0-flash')?.id || MODELS.gemini[0].id;

export async function generateTextGeminiClientSide(
    prompt: string,
    model: string = DEFAULT_CLIENT_GEMINI_MODEL_ID, // Use the default from config
    maxOutputTokens: number = 2000
): Promise<{ text: string } | { error: string }> { // Return shape indicating success or error

    // Check if the API key was loaded correctly from .env
    if (!GEMINI_API_KEY) {
        console.error("VITE_GEMINI_API_KEY not found. Make sure it's set in your .env file and the server was restarted.");
        return { error: "API Key not configured. Check environment variables." };
    }

    const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${GEMINI_API_KEY}`;

    const data = {
        contents: [{
            parts: [{
                text: prompt
            }]
        }],
        generationConfig: {
            // Adjust safety settings if needed, though defaults are usually reasonable
            // "safetySettings": [
            //     { "category": "HARM_CATEGORY_HARASSMENT", "threshold": "BLOCK_MEDIUM_AND_ABOVE" },
            //     { "category": "HARM_CATEGORY_HATE_SPEECH", "threshold": "BLOCK_MEDIUM_AND_ABOVE" },
            //     { "category": "HARM_CATEGORY_SEXUALLY_EXPLICIT", "threshold": "BLOCK_MEDIUM_AND_ABOVE" },
            //     { "category": "HARM_CATEGORY_DANGEROUS_CONTENT", "threshold": "BLOCK_MEDIUM_AND_ABOVE" }
            // ],
            maxOutputTokens: maxOutputTokens,
            // "temperature": 0.7, // Optional: Adjust creativity
            // "topP": 0.9, // Optional: Adjust token sampling
            // "topK": 40, // Optional: Adjust token sampling
        }
    };

    const httpOptions = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
    };

    try {
        console.log(`Calling Client-Side Gemini (${model})...`);
        const response = await fetch(url, httpOptions);

        if (!response.ok) {
            // Attempt to parse error response from Google API
            let errorBody: any = { message: `HTTP error! Status: ${response.status}` };
            try {
                errorBody = await response.json();
            } catch (parseError) {
                // If parsing fails, use the status text
                errorBody.message += ` - ${response.statusText}`;
            }
            console.error('Error fetching data from Client-Side Gemini API:', errorBody);
            const errorMessage = errorBody?.error?.message || errorBody.message || 'Unknown API error';
            return { error: errorMessage };
        }

        const responseJson = await response.json() as GeminiResponse;

        // Basic validation of response structure
        if (
            !responseJson.candidates ||
            !responseJson.candidates[0] ||
            !responseJson.candidates[0].content ||
            !responseJson.candidates[0].content.parts ||
            !responseJson.candidates[0].content.parts[0] ||
            typeof responseJson.candidates[0].content.parts[0].text !== 'string'
        ) {
            console.error("Invalid response structure from Gemini API:", responseJson);
            return { error: "Received invalid or incomplete data structure from AI." };
        }

        const text = responseJson.candidates[0].content.parts[0].text;
        console.log("Client-Side Gemini Success.");
        return { text };

    } catch (error: any) {
        console.error('Network or other error calling Client-Side Gemini API:', error);
        return { error: error.message || 'Network error or failed to call API.' };
    }
} 